import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { RecommendationProducts } from '../interfaces/recommendation-products';
import { JobPlatform } from '../interfaces/job-platform';
import { JobPlatformsAdapter } from '../adapters/job-platforms.adapter';
import { TranslocoService } from '@jsverse/transloco';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class JobPlatformsService {
  private _object: BehaviorSubject<RecommendationProducts>;
  object$: Observable<RecommendationProducts>;

  initialState: RecommendationProducts = {
    currency: 'EUR',
  };
  state: RecommendationProducts = { ...this.initialState };
  defaultPlatforms: WritableSignal<JobPlatform[]> = signal([]);
  _recommendedPlatforms: WritableSignal<JobPlatform[]> = signal([]);
  recommendedPlatforms = computed(() =>
    this._recommendedPlatforms().map((platform) => ({
      ...platform,
    }))
  );
  recommendedPlatformsLoading: WritableSignal<boolean> = signal(false);
  private _platforms: WritableSignal<JobPlatform[]> = signal([]);
  platforms = computed(() =>
    this._platforms().map((platform) => ({
      ...platform,
    }))
  );
  platformsTotal: WritableSignal<number> = signal(0);
  platformsLoading: WritableSignal<boolean> = signal(false);

  private jobPlatformsAdapter = inject(JobPlatformsAdapter);
  private translateService = inject(TranslocoService);

  constructor() {
    this._object = new BehaviorSubject<RecommendationProducts>(this.state);
    this.object$ = this._object.asObservable();
  }

  loadDefaultPlatforms() {
    this.jobPlatformsAdapter
      .getDefaultJobPlatforms()
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this.defaultPlatforms.set(data.platforms);
      });
  }

  loadPlatforms(page: number, query: string) {
    this.platformsLoading.set(true);
    this.jobPlatformsAdapter
      .getJobPlatforms(
        page,
        10,
        query,
        this.state.currency,
        this.state.jobTitleId,
        '',
        this.state.exactLocationId,
        this.translateService.getActiveLang() ?? 'en'
      )
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this._platforms.set(data.platforms);
        this.platformsTotal.set(data.count);
        this.platformsLoading.set(false);
      });
  }

  loadRecommendedPlatforms() {
    this.recommendedPlatformsLoading.set(true);
    this.jobPlatformsAdapter
      .getRecommendedJobPlatforms(
        this.state.currency,
        this.state.jobTitleId,
        '',
        this.state.exactLocationId,
        this.translateService.getActiveLang() ?? 'en'
      )
      .pipe(untilDestroyed(this))
      .subscribe((data) => {
        this._recommendedPlatforms.set(data.platforms);
        this.recommendedPlatformsLoading.set(false);
      });
  }

  updateJobTitleId(jobTitleId: string) {
    const newState = { ...this.state, jobTitleId };
    this.state = newState;
    this.loadRecommendedPlatforms();
    this._object.next(newState);
  }

  updateLocationId(locationId: string) {
    const newState = { ...this.state, exactLocationId: locationId };
    this.state = newState;
    this.loadRecommendedPlatforms();
    this._object.next(newState);
  }
}
